import React, { useState, useEffect } from 'react';
import api from '../api';
import { AlertTriangle, Bell, DollarSign, Briefcase, UserX } from 'lucide-react';
import { format } from 'date-fns';

export const Defaults = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetch(); }, [filter]);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/defaults?status=${filter}`);
      setItems(res.data.data?.defaults || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const sendWarn = async (memberId) => {
    const msg = window.prompt('Custom warning message (leave blank for default):');
    if (msg === null) return;
    try {
      await api.post(`/admin/defaults/${memberId}/warn`, { message: msg || undefined });
      alert('Warning sent');
      fetch();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error?.message || e.message)); }
  };

  const addFee = async (memberId) => {
    const amount = window.prompt('Late fee amount:');
    if (!amount) return;
    try {
      await api.post(`/admin/defaults/${memberId}/late-fee`, { amount: Number(amount) });
      fetch();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error?.message || e.message)); }
  };

  const collections = async (memberId) => {
    if (!window.confirm('Escalate this member to collections?')) return;
    try {
      await api.post(`/admin/defaults/${memberId}/collections`);
      fetch();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error?.message || e.message)); }
  };

  const blacklistUser = async (userId) => {
    if (!window.confirm('Block this user from joining any future circles?')) return;
    try {
      await api.post(`/admin/users/${userId}/blacklist-circles`);
      fetch();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error?.message || e.message)); }
  };

  return (
    <div>
      <div className="card-header" style={{ padding: '0 0 24px 0', border: 'none' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Default Management</h1>
        <select className="dropdown" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All Defaulting</option>
          <option value="warned">Warned</option>
          <option value="collections">In Collections</option>
        </select>
      </div>

      <div className="glass">
        <div className="table-container" style={{ border: 'none', borderRadius: 16 }}>
          {loading ? <div className="loader-container"><div className="spinner"></div></div> : (
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Circle</th>
                  <th>Missed</th>
                  <th>Owed</th>
                  <th>Days Late</th>
                  <th>Members Impacted</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.memberId}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{d.user?.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.user?.email} · CiruScore {d.user?.ciruScore}</div>
                    </td>
                    <td>{d.circleName}</td>
                    <td><span className="badge rejected">{d.missedPayments}</span></td>
                    <td style={{ fontWeight: 600 }}>
                      ${d.amountOwed.toLocaleString()}
                      {d.lateFeeApplied > 0 && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>incl. ${d.lateFeeApplied.toLocaleString()} fee</div>}
                    </td>
                    <td>{d.daysLate !== null ? `${d.daysLate}d` : '—'}</td>
                    <td>{d.membersImpacted}</td>
                    <td>
                      <span className={`badge ${d.status.toLowerCase()}`}
                            style={d.status === 'COLLECTIONS' ? { background: 'rgba(239,68,68,0.2)', color: '#fca5a5' } :
                                   d.status === 'WARNED' ? { background: 'rgba(245,158,11,0.2)', color: '#fcd34d' } : undefined}>
                        {d.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-secondary" title="Send warning" onClick={() => sendWarn(d.memberId)} style={{ padding: '6px 10px' }}><Bell size={14} /></button>
                        <button className="btn-secondary" title="Add late fee" onClick={() => addFee(d.memberId)} style={{ padding: '6px 10px' }}><DollarSign size={14} /></button>
                        {!d.inCollections && (
                          <button className="btn-danger" title="Escalate to collections" onClick={() => collections(d.memberId)} style={{ padding: '6px 10px' }}><Briefcase size={14} /></button>
                        )}
                        {d.user && (
                          <button className="btn-danger" title="Block from future circles" onClick={() => blacklistUser(d.user.id)} style={{ padding: '6px 10px' }}><UserX size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                    <AlertTriangle size={24} style={{ marginBottom: 8, opacity: 0.5 }} /><div>No defaults — community is healthy</div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
